<template>
<div v-if="summary">
  <v-card-text>
    <span class="body-1">{{ $t('reviews.rate') }}</span>
    <div class="d-flex align-center mb-4 mt-1">
      <span class="font-weight-bold primary--text mr-2 pt-1">{{ summary.generalRate.average.toFixed(1) }}</span>
      <v-rating length="5" size="16" color="accent" readonly  :value="+summary.generalRate.average" class="mr-2" />
      <span class="pt-1">{{ summary.generalRate.reviewsCount }} {{ $t('reviews.grades') }} / {{ summary.generalRate.companiesReviewedCount }}  {{ $t('reviews.companies') }}</span>
    </div>
    <span class="body-1">{{ $t('reviews.rates') }}</span>
    <div class="mt-1" style="width: 350px">
      <v-row v-for="(item, name) in summary" :key="name" no-gutters>
        <template v-if="item.average > 0">
          <v-col cols="5">
            <span class="body-2">{{ $t(`reviews.${name}`) }}</span>
          </v-col>
          <v-col cols="4" align-self="end">
            <v-rating length="5" size="16" color="accent" readonly :value="+item.average" class="mb-1 d-flex justify-end" />
          </v-col>
          <v-col cols="3">
            <span class="font-weight-bold primary--text ml-2">{{ item.average.toFixed(1) }}</span>
          </v-col>
        </template>
      </v-row>
    </div>
  </v-card-text>
  <v-divider />
  <v-card-text>
    <v-form ref="form">
      <v-row align="center">
        <v-col cols="12" md="3">
          <label>{{ $t('reviews.place') }}</label>
          <v-text-field v-model="searchInputValue" prepend-inner-icon="$magnify" outlined dense single-line hide-details="auto" />
        </v-col>
        <v-col cols="12" md="2">
          <DatePicker v-model="params.dateFrom" :label="$t('filters.dateFrom')" />
        </v-col>
        <v-col cols="12" md="2">
          <DatePicker v-model="params.dateTo" :label="$t('filters.dateTo')" />
        </v-col>
       </v-row>
    </v-form>
  </v-card-text>
  <v-divider />
  <data-table
    ref="dataTable"
    :headers="headers"
    :endpoint="`TSLReview/GetReviews`"
    :additionalParams="{ dateFrom: params.dateFrom, dateTo: params.dateTo, contractorId: contractorId }"
    serverPagination
  >
    <template v-slot:[`item.review`]="{ item }">
      <v-tooltip v-if="item.review" bottom min-width="310" max-width="310">
        <template v-slot:activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on" class="d-flex align-center">
            <span class="font-weight-bold primary--text mr-2 pt-1">{{ item.review.generalRate.toFixed(1) }}</span>
            <v-rating length="5" size="16" color="accent" readonly :value="+item.review.generalRate" class="mr-2" />
          </div>
          <span v-if="item.review" class="text">{{ item.review.comment | trimText(25) }}</span>
        </template>
        <v-row class="py-4">
          <v-col cols="6">
            <div class="body-2 mb-2">{{ $t('reviews.comment') }}</div>
            <div class="comment body-2">
              {{ item.review.comment | trimText(250) }}
            </div>
          </v-col>
          <v-col cols="6">
            <div class="body-2 mb-2">{{ $t('reviews.title') }}</div>
            <div class="body-2 mb-2">
              {{ $t('reviews.communicationRate') }}
              <v-rating length="5" size="16" color="accent" readonly :value="+item.review.communicationRate" />
            </div>
            <div class="body-2 mb-2">
              {{ $t('reviews.punctualityRate') }}
              <v-rating length="5" size="16" color="accent" readonly :value="+item.review.punctualityRate" />
            </div>
            <div class="body-2 mb-2">
              {{ $t('reviews.serviceQualityRate') }}
              <v-rating length="5" size="16" color="accent" readonly :value="+item.review.serviceQualityRate" />
            </div>
            <div class="body-2 mb-2">
              {{ $t('reviews.accordanceWithArrangementsRate') }}
              <v-rating length="5" size="16" color="accent" readonly :value="+item.review.accordanceWithArrangementsRate" />
            </div>
            <div class="body-2 mb-2">
              {{ $t('reviews.paymentRate') }}
              <v-rating length="5" size="16" color="accent" readonly :value="+item.review.paymentRate" />
            </div>
          </v-col>
        </v-row>
      </v-tooltip>
      <div v-else>
        <span>{{ $t('reviews.none') }}</span>
      </div>
    </template>
    <template v-slot:item.reviewDate="{ item }">
      <div class="text-center">
        {{ item.reviewDate | dateFormat('DD.MM.YYYY') }} <br/>
        {{ item.reviewDate | dateFormat('HH:mm') }}
      </div>
    </template>
    <template v-slot:item.actions="{ item }">
      <v-btn
        v-if="canEditReview(item)"
        icon
        small
        @click="openEditDialog(item)"
      >
        <v-icon small>$edit</v-icon>
      </v-btn>
    </template>
    <template v-slot:no-data>
      {{ $t("rating.noData") }}
    </template>
  </data-table>

  <EditReviewDialog
    v-model="editDialog"
    :review="selectedReview"
    @review-updated="onReviewUpdated"
  />
</div>
</template>

<script>
import Service from '@/services'
import DataTable from '@/components/DataTable'
import DatePicker from '@/components/forms/DatePicker'
import EditReviewDialog from './components/EditReviewDialog'

export default {
  name: 'Ratings',
  components: {
    DataTable,
    DatePicker,
    EditReviewDialog
  },
  props: {
    contractorId: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      params: {
        dateFrom: null,
        dateTo: null
      },
      summary: null,
      searchInputValue: '',
      editDialog: false,
      selectedReview: null,
      headers: [
        {
          text: this.$t('reviews.tabRate'),
          sortable: false,
          value: 'review'
        },
        {
          text: this.$t('reviews.created'),
          align: 'center',
          value: 'reviewDate'
        },
        {
          text: this.$t('common.actions'),
          align: 'center',
          sortable: false,
          value: 'actions'
        }
      ]
    }
  },
  async created () {
    await this.getDetails()
  },
  methods: {
    async getDetails () {
      this.loading = true

      try {
        const { data } = await Service.get(`/TSLReview/GetSummary?contractorId=${this.contractorId}`)
        this.summary = data
      } catch (e) {}

      this.loading = false
    },
    canEditReview (item) {
      return item.isEditable || false
    },
    openEditDialog (item) {
      this.selectedReview = item
      this.editDialog = true
    },
    async onReviewUpdated () {
      await this.getDetails()
      if (this.$refs.dataTable) {
        this.$refs.dataTable.refresh()
      }
    }
  }
}
</script>
<style>
.comment {
  width: 100%;
  padding: 8px 4px;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 5px;
}
</style>
